<div class="topic-share-container">
  <header class="topic-share-header">
    <h1>📊 Topic Share 2</h1>
    <p>Build universal entity list and check domain coverage</p>
  </header>

  <div class="topic-share-section">
    <div class="form-container">
      <div class="form-group">
        <label for="topic-input">Topic:</label>
        <input
          id="topic-input"
          type="text"
          [(ngModel)]="topic"
          placeholder="e.g., project management"
          class="topic-input"
          [disabled]="loading"
          (keyup.enter)="discoverCoreKeyword()"
        />
      </div>

      <div class="defaults-indicator">
        <span class="indicator-icon">🌍</span>
        <span class="indicator-text">
          Location: United States ({{ defaultLocationCode }}) | Language: {{ defaultLanguageName }}
        </span>
      </div>

      <button 
        (click)="discoverCoreKeyword()" 
        [disabled]="loading || !topic.trim()"
        class="discover-button"
      >
        <span *ngIf="!loading">Discover Core Keyword & SERP</span>
        <span *ngIf="loading">Discovering...</span>
      </button>
    </div>

    <div *ngIf="error" class="error-message">
      <strong>Error:</strong> {{ error }}
    </div>

    <!-- Core Keyword Result -->
    <div *ngIf="result && coreKeywordMetadata" class="results-container">
      <div class="result-card">
        <div class="result-header">
          <h2>Core Keyword Result</h2>
          <div class="metadata-badge" [class.from-database]="coreKeywordMetadata.dataSource === 'database'" [class.from-api]="coreKeywordMetadata.dataSource === 'api'">
            <span class="badge-icon">{{ coreKeywordMetadata.dataSource === 'database' ? '💾' : '🌐' }}</span>
            <span class="badge-text">
              {{ coreKeywordMetadata.dataSource === 'database' ? 'From Database' : 'From API' }}
            </span>
            <span *ngIf="coreKeywordMetadata.lastUpdated" class="badge-date">
              ({{ formatDate(coreKeywordMetadata.lastUpdated) }})
            </span>
          </div>
        </div>

        <div class="result-content">
          <div class="result-item">
            <label>Topic:</label>
            <span class="result-value">{{ result.topic }}</span>
          </div>
          <div class="result-item">
            <label>Core Keyword:</label>
            <span class="result-value core-keyword">{{ result.coreKeyword }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- SERP Results Section -->
    <div *ngIf="loadingSerp" class="loading-indicator">
      <span>Loading ranking domains...</span>
    </div>

    <div *ngIf="serpResults && serpMetadata" class="serp-results-container">
      <div class="serp-results-header">
        <h2>Ranking Domains (Top 10)</h2>
        <div class="metadata-badge" [class.from-database]="serpMetadata.dataSource === 'database'" [class.from-api]="serpMetadata.dataSource === 'api'">
          <span class="badge-icon">{{ serpMetadata.dataSource === 'database' ? '💾' : '🌐' }}</span>
          <span class="badge-text">
            {{ serpMetadata.dataSource === 'database' ? 'From Database' : 'From API' }}
          </span>
          <span *ngIf="serpMetadata.lastUpdated" class="badge-date">
            ({{ formatDate(serpMetadata.lastUpdated) }})
          </span>
        </div>
      </div>

      <div *ngIf="coreKeywordCost > 0 || serpCost > 0" class="cost-breakdown-section">
        <h3>Cost Breakdown</h3>
        <div class="cost-details">
          <div class="cost-item">
            <span class="cost-label">Core Keyword:</span>
            <span class="cost-value">${{ coreKeywordCost.toFixed(4) }}</span>
          </div>
          <div class="cost-item">
            <span class="cost-label">SERP:</span>
            <span class="cost-value">${{ serpCost.toFixed(4) }}</span>
          </div>
          <div class="cost-item cost-total">
            <span class="cost-label">Subtotal:</span>
            <span class="cost-value">${{ (coreKeywordCost + serpCost).toFixed(4) }}</span>
          </div>
        </div>
      </div>

      <div class="serp-list">
        <div *ngFor="let result of serpResults.results" class="serp-item">
          <div class="serp-position">{{ result.position }}</div>
          <div class="serp-content">
            <div class="serp-title">
              <a [href]="result.url" target="_blank" rel="noopener noreferrer">{{ result.title }}</a>
            </div>
            <div class="serp-url">
              <a [href]="result.url" target="_blank" rel="noopener noreferrer">{{ result.url }}</a>
            </div>
            <div class="serp-domain">{{ result.domain }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Build Universal Entity List Section -->
    <div *ngIf="serpResults && !loadingUniversalList" class="action-section">
      <button 
        (click)="buildUniversalEntityList()" 
        [disabled]="loadingUniversalList || !serpResults"
        class="action-button"
      >
        <span *ngIf="!loadingUniversalList">Build Universal Entity List</span>
        <span *ngIf="loadingUniversalList">Building...</span>
      </button>
    </div>

    <div *ngIf="loadingUniversalList" class="loading-indicator">
      <span>Building universal entity list...</span>
    </div>

    <div *ngIf="universalEntities.length > 0 && universalEntityListMetadata" class="universal-entity-list-container">
      <div class="section-header">
        <h2>Universal Entity List</h2>
        <div class="metadata-badge" [class.from-database]="universalEntityListMetadata.dataSource === 'database'" [class.from-api]="universalEntityListMetadata.dataSource === 'api'">
          <span class="badge-icon">{{ universalEntityListMetadata.dataSource === 'database' ? '💾' : '🌐' }}</span>
          <span class="badge-text">
            {{ universalEntityListMetadata.dataSource === 'database' ? 'From Database' : 'From API' }}
          </span>
          <span *ngIf="universalEntityListMetadata.lastUpdated" class="badge-date">
            ({{ formatDate(universalEntityListMetadata.lastUpdated) }})
          </span>
        </div>
      </div>

      <!-- Cost Breakdown -->
      <div *ngIf="universalEntityListCost" class="cost-breakdown-section">
        <h3>Cost Breakdown</h3>
        <div class="cost-details">
          <div class="cost-item">
            <span class="cost-label">Core Keyword:</span>
            <span class="cost-value">${{ (universalEntityListCost.coreKeyword || 0).toFixed(4) }}</span>
          </div>
          <div class="cost-item">
            <span class="cost-label">Related (depth=3):</span>
            <span class="cost-value">${{ (universalEntityListCost.relatedDepth3 || 0).toFixed(4) }}</span>
          </div>
          <div class="cost-item">
            <span class="cost-label">Suggestions:</span>
            <span class="cost-value">${{ (universalEntityListCost.suggestions || 0).toFixed(4) }}</span>
          </div>
          <div class="cost-item cost-total">
            <span class="cost-label">Total:</span>
            <span class="cost-value">${{ universalEntityListCost.total.toFixed(4) }}</span>
          </div>
        </div>
      </div>

      <!-- Stats -->
      <div *ngIf="universalEntityListStats" class="stats-section">
        <h3>Statistics</h3>
        <div class="stats-details">
          <div class="stat-item">
            <span class="stat-label">Total Discovered:</span>
            <span class="stat-value">{{ universalEntityListStats.totalDiscovered }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">After Deduplication:</span>
            <span class="stat-value">{{ universalEntityListStats.afterDeduplication }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">After Similarity Filter:</span>
            <span class="stat-value">{{ universalEntityListStats.afterSimilarityFilter }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">From Related (depth=3):</span>
            <span class="stat-value">{{ universalEntityListStats.fromRelatedDepth3 }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">From Suggestions:</span>
            <span class="stat-value">{{ universalEntityListStats.fromSuggestions }}</span>
          </div>
        </div>
      </div>

      <!-- Entities Table -->
      <div class="entities-table-container">
        <div class="table-header">
          <h3>Entities ({{ universalEntities.length }} total)</h3>
          <button 
            *ngIf="universalEntities.length > 50"
            (click)="showAllEntities = !showAllEntities" 
            class="toggle-button"
          >
            {{ showAllEntities ? 'Show Less' : 'Show All' }}
          </button>
        </div>
        <table class="entities-table">
          <thead>
            <tr>
              <th>Keyword</th>
              <th>Similarity Score</th>
              <th>Search Volume</th>
              <th>Keyword Difficulty</th>
              <th>Sources</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let entity of getDisplayedEntities()">
              <td class="entity-keyword">
                <strong>{{ entity.keyword }}</strong>
                <span *ngIf="entity.coreKeyword && entity.coreKeyword !== entity.keyword" class="core-keyword-badge">
                  (core: {{ entity.coreKeyword }})
                </span>
              </td>
              <td>
                <span class="similarity-score" [class]="getSimilarityColorClass(entity.similarityScore)">
                  {{ (entity.similarityScore * 100).toFixed(1) }}%
                </span>
              </td>
              <td>{{ entity.searchVolume.toLocaleString() }}</td>
              <td>{{ entity.keywordDifficulty }}</td>
              <td>
                <div class="sources-badges">
                  <span *ngFor="let source of entity.sources" class="source-badge">
                    {{ source.replace('_', ' ') }}
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Check Domain Coverage Section -->
    <div *ngIf="universalEntities.length > 0 && !loadingDomainCoverage" class="action-section">
      <button 
        (click)="checkDomainCoverage()" 
        [disabled]="loadingDomainCoverage || !universalEntities.length || !serpResults"
        class="action-button"
      >
        <span *ngIf="!loadingDomainCoverage">Check Domain Coverage</span>
        <span *ngIf="loadingDomainCoverage">Checking...</span>
      </button>
    </div>

    <div *ngIf="loadingDomainCoverage" class="loading-indicator">
      <span>Checking domain coverage...</span>
    </div>

    <div *ngIf="domainCoverage.length > 0 && domainCoverageMetadata" class="domain-coverage-container">
      <div class="section-header">
        <h2>Domain Coverage Summary</h2>
        <div class="metadata-badge" [class.from-database]="domainCoverageMetadata.dataSource === 'database'" [class.from-api]="domainCoverageMetadata.dataSource === 'api'">
          <span class="badge-icon">{{ domainCoverageMetadata.dataSource === 'database' ? '💾' : '🌐' }}</span>
          <span class="badge-text">
            {{ domainCoverageMetadata.dataSource === 'database' ? 'From Database' : 'From API' }}
          </span>
          <span *ngIf="domainCoverageMetadata.lastUpdated" class="badge-date">
            ({{ formatDate(domainCoverageMetadata.lastUpdated) }})
          </span>
        </div>
      </div>

      <!-- Cost Breakdown -->
      <div *ngIf="domainCoverageCost" class="cost-breakdown-section">
        <h3>Cost Breakdown</h3>
        <div class="cost-details">
          <div class="cost-item">
            <span class="cost-label">Ranked Keywords:</span>
            <span class="cost-value">${{ (domainCoverageCost.rankedKeywords || 0).toFixed(4) }}</span>
          </div>
          <div class="cost-item cost-total">
            <span class="cost-label">Total:</span>
            <span class="cost-value">${{ domainCoverageCost.total.toFixed(4) }}</span>
          </div>
        </div>
      </div>

      <!-- Stats -->
      <div *ngIf="domainCoverageStats" class="stats-section">
        <h3>Statistics</h3>
        <div class="stats-details">
          <div class="stat-item">
            <span class="stat-label">Total Entities:</span>
            <span class="stat-value">{{ domainCoverageStats.totalEntities }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Total Domains:</span>
            <span class="stat-value">{{ domainCoverageStats.totalDomains }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Total API Calls:</span>
            <span class="stat-value">{{ domainCoverageStats.totalApiCalls }}</span>
          </div>
        </div>
      </div>

      <!-- Domain Coverage Table -->
      <div class="domain-coverage-table-container">
        <table class="domain-coverage-table">
          <thead>
            <tr>
              <th>Domain</th>
              <th>Entities Covered</th>
              <th>Total Keywords Ranked</th>
              <th>Coverage %</th>
              <th>URLs Ranking</th>
              <th>Missing Entities</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let domain of domainCoverage">
              <td class="domain-name">{{ domain.domain }}</td>
              <td class="entities-count">
                <span class="count-value">{{ domain.entitiesCovered }}</span>
                <span class="count-total">/ {{ domainCoverageStats?.totalEntities || 0 }}</span>
                <span class="count-percentage">({{ domain.coveragePercentage.toFixed(1) }}%)</span>
              </td>
              <td class="total-keywords">
                <span class="count-value">{{ domain.totalKeywordsRanked.toLocaleString() }}</span>
              </td>
              <td class="coverage-percentage">
                <span class="percentage-value">{{ domain.coveragePercentage.toFixed(1) }}%</span>
              </td>
              <td class="urls-count">
                <span class="count-value">{{ domain.urls.length }}</span>
                <div *ngIf="domain.urls.length > 0" class="urls-list">
                  <div *ngFor="let url of domain.urls.slice(0, 5)" class="url-item">
                    <a [href]="url.url" target="_blank" rel="noopener noreferrer" class="url-link">
                      {{ url.title }}
                    </a>
                    <span class="url-meta">(pos: {{ url.position }}, entities: {{ url.entitiesCount }})</span>
                  </div>
                  <div *ngIf="domain.urls.length > 5" class="url-more">
                    + {{ domain.urls.length - 5 }} more URLs
                  </div>
                </div>
              </td>
              <td class="missing-entities">
                <div *ngIf="domain.missingEntities.length > 0" class="missing-list">
                  <span *ngFor="let entity of domain.missingEntities.slice(0, 10)" class="missing-item">
                    {{ entity }}
                  </span>
                  <span *ngIf="domain.missingEntities.length > 10" class="missing-more">
                    + {{ domain.missingEntities.length - 10 }} more
                  </span>
                </div>
                <span *ngIf="domain.missingEntities.length === 0" class="no-missing">None</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Total Cost Display -->
    <div *ngIf="totalCost > 0" class="total-cost-section">
      <h3>Total Cost</h3>
      <div class="total-cost-value">${{ totalCost.toFixed(4) }}</div>
    </div>
  </div>
</div>

